import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Stack,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  AutoFixHigh as EnrichIcon,
  Straighten as StandardizeIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckIcon,
  Cancel as MissingIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
} from '@mui/icons-material';
import {
  getEnrichmentStats,
  getIncompleteRecipes,
  batchEnrichRecipes,
  getEnrichmentStatus,
  enrichSingleRecipe,
  standardizeAllServings,
  EnrichmentStats,
  IncompleteRecipe,
  BatchEnrichmentResult,
  StandardizeResult,
} from '../adminApi';

const RecipeEnrichment: React.FC = () => {
  const [stats, setStats] = useState<EnrichmentStats | null>(null);
  const [incompleteRecipes, setIncompleteRecipes] = useState<IncompleteRecipe[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Batch enrichment state
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchProgress, setBatchProgress] = useState<string | null>(null);
  const [lastBatchResult, setLastBatchResult] = useState<BatchEnrichmentResult | null>(null);
  const [showErrors, setShowErrors] = useState(false);

  // Standardize state
  const [standardizeRunning, setStandardizeRunning] = useState(false);
  const [lastStandardizeResult, setLastStandardizeResult] = useState<StandardizeResult | null>(null);

  // Individual enrichment
  const [enrichingId, setEnrichingId] = useState<number | null>(null);

  // Incomplete recipes table
  const [showIncomplete, setShowIncomplete] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsData, recipesData] = await Promise.all([
        getEnrichmentStats(),
        getIncompleteRecipes(50),
      ]);
      setStats(statsData);
      setIncompleteRecipes(recipesData);
    } catch (err: any) {
      setError(err.message || 'Failed to load enrichment data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleBatchEnrich = async (stubsOnly = false) => {
    setBatchRunning(true);
    setBatchProgress('Starting batch...');
    setError(null);
    setSuccess(null);
    setLastBatchResult(null);

    try {
      const { jobId } = await batchEnrichRecipes({ limit: 100, stubsOnly });

      // Poll for progress
      const poll = async () => {
        try {
          const status = await getEnrichmentStatus(jobId);
          const r = status.results;
          setBatchProgress(
            `Processing ${r.processed}${status.total ? `/${status.total}` : ''} — ${r.succeeded} enriched, ${r.failed} failed`
          );
          setLastBatchResult(r);

          if (status.status === 'done') {
            setSuccess(
              `Batch complete: ${r.succeeded} enriched, ${r.skipped} skipped, ${r.failed} failed`
            );
            setBatchRunning(false);
            setBatchProgress(null);
            await fetchData();
          } else if (status.status === 'error') {
            setError(status.error || 'Batch enrichment failed');
            setBatchRunning(false);
            setBatchProgress(null);
          } else {
            setTimeout(poll, 3000);
          }
        } catch (err: any) {
          setError(err.message || 'Failed to check enrichment status');
          setBatchRunning(false);
          setBatchProgress(null);
        }
      };

      setTimeout(poll, 2000);
    } catch (err: any) {
      setError(err.message || 'Batch enrichment failed');
      setBatchRunning(false);
      setBatchProgress(null);
    }
  };

  const handleStandardize = async () => {
    setStandardizeRunning(true);
    setError(null);
    setSuccess(null);
    setLastStandardizeResult(null);

    try {
      const result = await standardizeAllServings();
      setLastStandardizeResult(result);
      setSuccess(
        `Standardization complete: ${result.standardized} updated, ${result.failed} failed`
      );
      await fetchData();
    } catch (err: any) {
      setError(err.message || 'Standardization failed');
    } finally {
      setStandardizeRunning(false);
    }
  };

  const handleEnrichSingle = async (recipeId: number) => {
    setEnrichingId(recipeId);
    setError(null);

    try {
      const result = await enrichSingleRecipe(recipeId);
      if (result.skipped) {
        setSuccess(`Recipe #${recipeId} already complete`);
      } else {
        setSuccess(`Recipe #${recipeId} ${result.status} successfully`);
      }
      await fetchData();
    } catch (err: any) {
      setError(`Failed to enrich recipe #${recipeId}: ${err.message}`);
    } finally {
      setEnrichingId(null);
    }
  };

  if (loading && !stats) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Recipe Enrichment
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Generate steps, tags, and nutrition for existing recipes
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchData}
          disabled={loading || batchRunning || standardizeRunning}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Stats Cards */}
      {stats && (
        <Box
          display="grid"
          gridTemplateColumns="repeat(auto-fit, minmax(160px, 1fr))"
          gap={2}
          mb={3}
        >
          <StatCard label="Total Recipes" value={stats.total} />
          <StatCard label="With Ingredients" value={stats.with_ingredients} total={stats.total} color="primary" />
          <StatCard label="With Steps" value={stats.with_steps} total={stats.total} color="primary" />
          <StatCard label="With Tags" value={stats.with_tags} total={stats.total} color="primary" />
          <StatCard label="With Nutrition" value={stats.with_nutrition} total={stats.total} color="primary" />
          <StatCard label="Standard Servings (4)" value={stats.standard_servings} total={stats.total} color="info" />
          <StatCard label="Stubs (no ingredients)" value={stats.stubs} color="error" />
        </Box>
      )}

      {/* Batch Enrichment */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Batch Enrichment
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Process incomplete recipes in batches of 100. Each batch takes ~3-5 minutes (5 recipes at a time with 2s delays).
        </Typography>

        {batchRunning && (
          <Box sx={{ my: 2 }}>
            <LinearProgress />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              {batchProgress || 'Processing... this may take a few minutes'}
            </Typography>
          </Box>
        )}

        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
          <Button
            variant="contained"
            startIcon={batchRunning ? <CircularProgress size={18} color="inherit" /> : <EnrichIcon />}
            onClick={() => handleBatchEnrich(false)}
            disabled={batchRunning || standardizeRunning}
          >
            {batchRunning ? 'Processing...' : 'Enrich Next 100'}
          </Button>
          {stats && stats.stubs > 0 && (
            <Button
              variant="outlined"
              startIcon={<EnrichIcon />}
              onClick={() => handleBatchEnrich(true)}
              disabled={batchRunning || standardizeRunning}
            >
              Expand {stats.stubs} Stubs Only
            </Button>
          )}
        </Stack>

        {/* Last batch result */}
        {lastBatchResult && (
          <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Last Batch Result
            </Typography>
            <Stack direction="row" spacing={2}>
              <Chip label={`${lastBatchResult.processed} processed`} variant="outlined" />
              <Chip label={`${lastBatchResult.succeeded} succeeded`} color="success" />
              <Chip label={`${lastBatchResult.skipped} skipped`} color="info" variant="outlined" />
              {lastBatchResult.failed > 0 && (
                <Chip
                  label={`${lastBatchResult.failed} failed`}
                  color="error"
                  onClick={() => setShowErrors(!showErrors)}
                />
              )}
            </Stack>
            <Collapse in={showErrors && lastBatchResult.errors.length > 0}>
              <Box sx={{ mt: 2 }}>
                {lastBatchResult.errors.map((err, i) => (
                  <Typography key={i} variant="body2" color="error" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    #{err.recipe_id} {err.name}: {err.error}
                  </Typography>
                ))}
              </Box>
            </Collapse>
          </Paper>
        )}
      </Paper>

      {/* Serving Standardization */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Serving Standardization
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Normalize all recipe servings to 4. Scales ingredient quantities proportionally. No AI calls needed.
          {stats && stats.non_standard_servings > 0 && (
            <> <strong>{stats.non_standard_servings}</strong> recipes need standardization.</>
          )}
        </Typography>

        {standardizeRunning && (
          <Box sx={{ my: 2 }}>
            <LinearProgress />
          </Box>
        )}

        <Button
          variant="contained"
          color="secondary"
          startIcon={standardizeRunning ? <CircularProgress size={18} color="inherit" /> : <StandardizeIcon />}
          onClick={handleStandardize}
          disabled={batchRunning || standardizeRunning || (stats?.non_standard_servings === 0)}
          sx={{ mt: 1 }}
        >
          {standardizeRunning ? 'Standardizing...' : 'Standardize All Servings'}
        </Button>

        {lastStandardizeResult && (
          <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
            <Stack direction="row" spacing={2}>
              <Chip label={`${lastStandardizeResult.processed} processed`} variant="outlined" />
              <Chip label={`${lastStandardizeResult.standardized} standardized`} color="success" />
              {lastStandardizeResult.failed > 0 && (
                <Chip label={`${lastStandardizeResult.failed} failed`} color="error" />
              )}
            </Stack>
          </Paper>
        )}
      </Paper>

      {/* Incomplete Recipes Table */}
      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            Incomplete Recipes ({incompleteRecipes.length})
          </Typography>
          <IconButton onClick={() => setShowIncomplete(!showIncomplete)}>
            {showIncomplete ? <CollapseIcon /> : <ExpandIcon />}
          </IconButton>
        </Box>

        <Collapse in={showIncomplete}>
          {incompleteRecipes.length === 0 ? (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
              All recipes are complete!
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={60}>ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell align="center">Servings</TableCell>
                    <TableCell align="center">Ingredients</TableCell>
                    <TableCell align="center">Steps</TableCell>
                    <TableCell align="center">Tags</TableCell>
                    <TableCell align="center">Nutrition</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {incompleteRecipes.map((recipe) => (
                    <TableRow key={recipe.recipe_id}>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {recipe.recipe_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {recipe.name}
                        {!recipe.has_ingredients && (
                          <Chip size="small" label="STUB" color="error" sx={{ ml: 1 }} />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          size="small"
                          label={recipe.base_servings || '?'}
                          color={recipe.base_servings === 4 ? 'success' : 'warning'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        {recipe.has_ingredients ? <CheckIcon color="success" fontSize="small" /> : <MissingIcon color="error" fontSize="small" />}
                      </TableCell>
                      <TableCell align="center">
                        {recipe.has_steps ? <CheckIcon color="success" fontSize="small" /> : <MissingIcon color="error" fontSize="small" />}
                      </TableCell>
                      <TableCell align="center">
                        {recipe.has_tags ? <CheckIcon color="success" fontSize="small" /> : <MissingIcon color="error" fontSize="small" />}
                      </TableCell>
                      <TableCell align="center">
                        {recipe.has_nutrition ? <CheckIcon color="success" fontSize="small" /> : <MissingIcon color="error" fontSize="small" />}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={enrichingId === recipe.recipe_id ? <CircularProgress size={14} /> : <EnrichIcon />}
                          onClick={() => handleEnrichSingle(recipe.recipe_id)}
                          disabled={enrichingId !== null || batchRunning}
                        >
                          Enrich
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Collapse>
      </Paper>
    </Box>
  );
};

// Stat card helper component
const StatCard: React.FC<{
  label: string;
  value: number;
  total?: number;
  color?: 'primary' | 'error' | 'info';
}> = ({ label, value, total, color }) => {
  const percentage = total ? Math.round((value / total) * 100) : null;

  return (
    <Paper sx={{ p: 2, textAlign: 'center' }}>
      <Typography variant="h4" fontWeight="bold" color={color ? `${color}.main` : 'text.primary'}>
        {value}
      </Typography>
      {percentage !== null && (
        <Typography variant="caption" color="text.secondary">
          {percentage}%
        </Typography>
      )}
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Paper>
  );
};

export default RecipeEnrichment;
